import React, { useState } from 'react';
import athlete_skeleton from './athlete_skeleton.png';
import { motion } from 'framer-motion';
import { CountryFlag } from '../../Utils/flags';
import { secondsInPast } from '../../Utils/time';
import style from './results.module.scss';
import { List as Advertisement } from '../Advertisement/list';
import { Tooltip } from '../Tooltip';
import { getTrans } from '../../Utils/translate';
import { AppContext } from '../../App';
import { getCrosslinkClass, handleClickAthleteName } from '../../Utils/crosslink';
import { CosmosImage } from '../../cosmos/web-scoped/react';

const Athlete = ({
  athlete,
  index,
  listType,
  athlete_id,
  result,
  previous_round_results,
  delaySeconds,
  result_changes,
  start,
  advertising,
  animate,
  round_number,
  total_points,
  rounds,
  toolTipContent,
  toolTipOn,
  showAthleteImage = true,
  crosslinkDetails
}) => {
  const context = React.useContext(AppContext);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState();
  const [delayHandler, setDelayHandler] = useState();
  const { run1, run2 } = context.lang;

  const { first_name, last_name, nationality } = athlete;
  const { iso3 } = nationality;
  let resultDate;
  let inPast = false;
  let rank = result.rank;
  let showPoints = total_points;

  if (result_changes) {
    //setRank
    const rankChange = result_changes.filter((change) => change.type === 'rank')[0];
    if (rankChange) {
      rank = secondsInPast(new Date(rankChange.updated), delaySeconds || 60)
        ? rankChange.current
        : rankChange.previous;
      rank = parseInt(rank, 10);
    }
  }

  if (listType === 'cliff') {
    if (result && result.result_time) {
      resultDate = result.result_time ? new Date(result.result_time) : false;
      if (resultDate) {
        inPast = secondsInPast(resultDate, delaySeconds || 60);
      }

      if (!inPast && total_points) {
        inPast = showPoints - result.points > 0;
        //TODO: discuss with BE
        // const prevRound = previous_round_results.find(
        //   (res) => res.round_number === round_number - 1
        // );
        // rank = prevRound.rank;
        showPoints -= result.points;
      }
    } else if (total_points) {
      inPast = true;
    } else {
      inPast = false;
    }
  }

  if (listType === 'lite' && result) {
    resultDate = new Date(result.result_time ? result.result_time : result);
    inPast = secondsInPast(resultDate, delaySeconds || 60);
  }

  const getSplitText = (text) => {
    let temp;

    if (text && typeof text === 'string') {
      temp = text.split(':');
      if (temp[0] === 'Run 1') {
        return (
          <span className={getTrans(run1).length >= 8 ? style.letterSpacing : ''}>
            {getTrans(run1)}: {temp[1]}
          </span>
        );
      }
      if (temp[0] === 'Run 2') {
        return (
          <span className={getTrans(run2).length >= 8 ? style.letterSpacing : ''}>
            {getTrans(run2)}: {temp[1]}
          </span>
        );
      }
    }
    return text;
  };

  return (
    <React.Fragment>
      {inPast && (
        <React.Fragment>
          <motion.tr
            className={`${style.athlete} ${index % 2 === 0 ? 'odd' : 'even'}`}
            initial={{ opacity: 0 }}
            transition={{
              opacity: {
                duration: animate && listType !== 'start' ? 3 : 0.5,
                delay: animate && listType !== 'start' ? 3.5 : 0
              },
              height: {
                duration: 1,
                delay: animate && listType !== 'start' ? 2 : 0
              }
            }}
            animate={{ opacity: 1 }}
            key={last_name}
            style={{ zIndex: -10 }}>
            <td
              className={`${style.rank}${
                index < 3 ? ' text-white' : ''
              } font-mono text-right`}>
              <b>{listType === 'start' ? start.order : rank !== 0 ? `${rank}.` : ' '}</b>
            </td>
            {showAthleteImage && (
              <td
                onClick={(e) => { handleClickAthleteName(e, athlete.id, crosslinkDetails); }}
                className={` ${style.athleteImage} ${getCrosslinkClass(context, crosslinkDetails)}`}>
                <div>
                  <CosmosImage
                    image={athlete.image ? (athlete.image.profile || athlete_skeleton) : athlete_skeleton}
                    image-preview={athlete_skeleton}
                    image-alt={`image-${athlete.first_name}-${athlete.last_name}`}/>
                  </div>
              </td>
            )}
            <td
              className={`${style.name} ${
                listType === 'lap' ? `${style.nameSmall} ` : ''
              }`}>
              <span
                onClick={(e) => { handleClickAthleteName(e, athlete.id, crosslinkDetails); }}
                className={`text-white ${getCrosslinkClass(context, crosslinkDetails)}`}>
                <img className="flag" src={CountryFlag(iso3)} alt={iso3} />
                {first_name && first_name.toLowerCase()}{' '}
                {last_name && last_name.toLowerCase()}{' '}
                {athlete.wildcard && <span style={{ fontSize: "0.9rem" }}> (WC)</span>}
              </span>
              <div
                className={`${style.judges}`}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.parentElement.getBoundingClientRect();
                  setTooltipCoords({
                    left: rect.x + rect.width / 2,
                    top: rect.y + window.scrollY - 105
                  });
                  setDelayHandler(
                    setTimeout(() => {
                      setShowTooltip(true);
                    }, 500)
                  );
                }}
                onMouseLeave={() => {
                  clearTimeout(delayHandler);
                  setShowTooltip(false);
                }}>
                {listType === 'lite' &&
                  result.result_subline &&
                  result.result_subline.map((text, index) => {
                    return (
                      <span
                        key={`${athlete_id}-subline-${index}`}>
                        {getSplitText(text)}
                      </span>
                    );
                  })}
                {listType === 'cliff' && (
                  <>
                    {round_number > 1 &&
                      previous_round_results.map((prevRound, index) => (
                        <span
                          key={`${athlete_id}-subline-${index}`}
                          className={`font-mono ${style.cliff}${
                            prevRound.event_topscore ? ` ${style.topScore}` : ''
                          }`}>
                          {!isNaN(parseFloat(prevRound.points))
                            ? parseFloat(prevRound.points).toFixed(2)
                            : prevRound.points}
                        </span>
                      ))}
                    <span
                      key={`${athlete_id}-subline`}
                      className={`font-mono ${style.cliff}${
                        result.event_topscore ? ` ${style.topScore}` : ''
                      }`}>
                      {showPoints === total_points
                        ? !isNaN(parseFloat(result.points))
                          ? parseFloat(result.points).toFixed(2)
                          : result.points
                        : ''}
                    </span>
                    {round_number < rounds &&
                      [...Array(parseInt(rounds) - parseInt(round_number))].map(
                        (_, index) => (
                          <span
                            key={`${athlete_id}-filler-${index}`}
                            className={style.cliff}>
                            &nbsp;
                          </span>
                        )
                      )}
                  </>
                )}
                {toolTipOn && showTooltip && (
                  <Tooltip coords={tooltipCoords} updateTooltipCoords={setTooltipCoords}>
                    <table
                      style={{ width: 160, fontSize: 12, border: 0 }}
                      cellSpacing={0}
                      cellPadding={0}>
                      <tbody>
                        {Object.keys(toolTipContent).map((key, index) => {
                          return (
                            <tr key={key}>
                              <td
                                style={{ lineHeight: '16px', width: 10 }}
                                className="text-white">
                                {key}
                              </td>
                              <td
                                style={{
                                  lineHeight: '16px',
                                  width: 25,
                                  textAlign: 'center'
                                }}
                                className="text-white">
                                {key ? '-' : ''}
                              </td>
                              <td
                                style={{ lineHeight: '16px', width: 55 }}
                                className="text-white">
                                {toolTipContent[key]}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Tooltip>
                )}
              </div>
            </td>
            <td className={`${style.time}${index < 3 ? ' text-white' : ''} font-mono`}>
              &nbsp;
              <div className={`${style.judgesScore}`}>
                <b>
                  {!isNaN(parseFloat(showPoints))
                    ? parseFloat(showPoints).toFixed(2)
                    : showPoints}
                </b>
              </div>
            </td>
          </motion.tr>
          <Advertisement advertising={advertising} show={animate} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export { Athlete };
